// ** react imports
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// ** router imports
import { useNavigate } from 'react-router-dom';

// ** utils imports
import { formatUtcToLocal } from '../../../utils/Utils';

// ** enums imports
import { ACCOUNT_TYPES } from '../../../enums/common';

function CustomersTableItem(props) {
  // props
  const { data, isSelected, onDeleteClick } = props;

  // states for dropdown menu
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // refs
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // hooks
  const navigate = useNavigate();

  // close dropdown on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close dropdown on escape key
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  // Helper functions
  const formatPhoneNumber = (countryCode, phone) => {
    if (!phone) return '-';
    const raw = String(phone).trim();
    const digits = raw.replace(/\D/g, '');

    // Handle US-style formatting
    let formatted = raw;
    if (digits.length === 10) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      const d = digits.slice(1);
      formatted = `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
      // Default to +1 if not provided
      if (!countryCode) countryCode = '+1';
    }

    return countryCode ? `${countryCode} ${formatted}` : formatted;
  };

  const getAccountTypeLabel = accountType => {
    switch (accountType) {
      case ACCOUNT_TYPES.REGISTER:
        return 'Registered';
      case ACCOUNT_TYPES.GUEST:
        return 'Guest';
      default:
        return accountType || '-';
    }
  };

  const getStatusBadge = () => {
    const isVerified = data?.isEmailVerified && data?.isPasswordSet;
    const isActive = data?.isActive;

    if (!isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          Inactive
        </span>
      );
    }

    if (isVerified) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          Verified
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
        Pending
      </span>
    );
  };

  return (
    <tr
      className={`${
        isSelected ? 'bg-violet-50 dark:bg-violet-900/20' : ''
      } hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors cursor-pointer`}
      onClick={() => navigate(`/customers/details/${data?._id}?page=profile`)}
    >
      {/* Customer ID */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-left font-medium text-violet-600 dark:text-violet-400 cursor-pointer hover:text-violet-700 dark:hover:text-violet-300 hover:underline transition-colors">
          #{data?.userId || 'N/A'}
        </div>
      </td>

      {/* Full Name */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-full flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-violet-600 dark:text-violet-400">
              {data?.fullName ? data.fullName.charAt(0).toUpperCase() : '?'}
            </span>
          </div>
          <div className="font-medium text-gray-800 dark:text-gray-100">
            {data?.fullName || 'N/A'}
          </div>
        </div>
      </td>

      {/* Email */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <div className="text-left text-gray-800 dark:text-gray-100">
            {data?.email || 'N/A'}
          </div>
          {data?.isEmailVerified && (
            <svg
              className="w-4 h-4 text-green-500 ml-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </td>

      {/* Phone Number */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-center text-gray-800 dark:text-gray-100">
          {formatPhoneNumber(data?.countryCode, data?.phone)}
        </div>
      </td>

      {/* Account Type */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          {getAccountTypeLabel(data?.accountType)}
        </span>
      </td>

      {/* Status */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        {getStatusBadge()}
      </td>

      {/* Joined Date */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-gray-800 dark:text-gray-100">
          {formatUtcToLocal(data?.createdAt)}
        </div>
      </td>

      {/* Actions */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
        <div className="flex items-center justify-center">
          <button
            className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 rounded-full p-1 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(data);
            }}
          >
            <span className="sr-only">Delete</span>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7ZM17 6H7V19H17V6ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
}

// PropTypes for better type checking
CustomersTableItem.propTypes = {
  data: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onDeleteClick: PropTypes.func,
};

export default CustomersTableItem;
